import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { Camera, CameraOff } from 'lucide-react';

const READER_ID = 'qr-camera-reader';

export const QrScanner = ({ onScan, onError }) => {
  const scannerRef = useRef(null);
  const [active, setActive] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const stop = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try {
        if (s.isScanning) await s.stop();
        await s.clear();
      } catch (_) {
        // ignore
      }
    }
    setActive(false);
  };

  const start = async () => {
    setError('');
    setStarting(true);
    try {
      const scanner = new Html5Qrcode(READER_ID, { verbose: false });
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' }, // prefer rear camera
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decodedText) => {
          // Stop after first decode so we don't spam the API
          onScan?.(decodedText);
          stop();
        },
        () => {
          // Per-frame failures are noisy; ignore
        }
      );
      setActive(true);
    } catch (e) {
      const msg =
        e?.message ||
        'Could not start the camera. Please allow camera permission and use HTTPS.';
      setError(msg);
      onError?.(msg);
      await stop();
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-3" data-testid="qr-scanner">
      <div
        id={READER_ID}
        className="w-full max-w-sm mx-auto rounded-md overflow-hidden bg-[#0f172a]"
        style={{ minHeight: active ? 260 : 0 }}
      />

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md" data-testid="qr-scanner-error">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="flex justify-center">
        {!active ? (
          <Button
            type="button"
            onClick={start}
            disabled={starting}
            className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
            data-testid="start-camera-button"
          >
            <Camera className="mr-2 h-4 w-4" />
            {starting ? 'Starting camera…' : 'Start Camera Scan'}
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={stop}
            data-testid="stop-camera-button"
          >
            <CameraOff className="mr-2 h-4 w-4" />
            Stop Camera
          </Button>
        )}
      </div>

      <p className="text-xs text-center text-[#64748b]">
        Point the rear camera at the visitor's QR code. Requires HTTPS + camera permission.
      </p>
    </div>
  );
};
